import type { OrderResponse, PaymentStatus, PaymentType } from '@/features/storefront/types/checkout';
import type { Restaurant } from '@/features/restaurants/types';
import type { PrintOrderPayload, PrintPaymentStatus } from '@/shared/types/electron';
import { restaurantHeaderFields } from './buildSampleOrder';

/**
 * Map the backend's `PaymentStatus` + `PaymentType` to the printer's banner
 * vocabulary. The receipt only needs to answer "did the customer pay?" /
 * "does the courier collect cash?" / "was this refunded?".
 */
function paymentStatusForReceipt(
  paymentStatus: PaymentStatus | undefined,
  paymentType: PaymentType | undefined,
): PrintPaymentStatus | undefined {
  if (paymentStatus === 'REFUNDED' || paymentStatus === 'PARTIALLY_REFUNDED') {
    return 'REFUNDED';
  }
  if (paymentStatus === 'SUCCEEDED') return 'PAID';
  if (paymentType === 'CASH') return 'CASH_ON_DELIVERY';
  if (paymentStatus === 'PENDING' || paymentStatus === 'PROCESSING') return 'PENDING';
  if (paymentStatus === 'FAILED' || paymentStatus === 'CANCELLED') return 'UNPAID';
  return undefined;
}

function paymentTypeLabel(paymentType: PaymentType | undefined): string | undefined {
  if (!paymentType) return undefined;
  const map: Record<string, string> = {
    CASH: 'Cash',
    CARD: 'Card',
    ONLINE: 'Online',
  };
  return map[paymentType] ?? paymentType;
}

/**
 * Convert a live `OrderResponse` (the shape the orders API returns) into the
 * `PrintOrderPayload` the receipt template expects. Restaurant header fields
 * come from the currently-selected `Restaurant` so the masthead matches the
 * dashboard.
 */
export function buildOrderPayload(
  order: OrderResponse,
  restaurant: Restaurant | null | undefined,
): PrintOrderPayload {
  const header = restaurantHeaderFields(restaurant);
  const items: PrintOrderPayload['items'] = (order.orderItems ?? []).map((item) => ({
    name: item.menuItemName ?? 'Item',
    variantName: item.variantName,
    quantity: item.quantity,
    unitPrice: item.unitPrice ?? 0,
    itemNotes: item.itemNotes,
    addOns: item.selectedAddOns?.map((a) => ({
      name: a.addOnName ?? 'Add-on',
      variantName: a.variantName,
      price: a.price,
    })),
  }));

  return {
    orderId: order.orderId,
    orderShortId: order.orderNumber ?? order.orderId.slice(0, 8),
    orderType: order.orderType,
    paymentType: paymentTypeLabel(order.paymentType),
    paymentStatus: paymentStatusForReceipt(order.paymentStatus, order.paymentType),
    customer: order.customerInfo
      ? {
          name: order.customerInfo.name,
          phone: order.customerInfo.phone,
          email: order.customerInfo.email,
        }
      : undefined,
    items,
    subtotal: order.subtotal,
    deliveryFee: order.deliveryFee,
    tax: order.tax,
    discountAmount: order.discountAmount,
    discountName: order.discountName,
    total: order.total ?? 0,
    deliveryAddress: order.deliveryAddress
      ? {
          street: order.deliveryAddress.street,
          streetNumber: order.deliveryAddress.streetNumber,
          houseNumber: order.deliveryAddress.houseNumber,
          postalCode: order.deliveryAddress.postalCode,
          city: order.deliveryAddress.city,
          country: order.deliveryAddress.country,
          deliveryInstructions: order.deliveryAddress.deliveryInstructions,
        }
      : undefined,
    orderNotes: order.orderNotes,
    createdAt: order.createdAt,
    estimatedReadyTime: order.estimatedReadyTime,
    ...header,
  };
}
